import {
  Component,
  input,
  output,
  computed,
  signal,
  effect,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { VOneIconComponent, VOneChipsTagsComponent } from '@shared-components';
import { IconType } from '@shared-enums';
import { CellConfig } from '@shared-models';

@Component({
  selector: 'v-one-table-cell',
  imports: [
    FormsModule,
    forwardRef(() => VOneIconComponent),
    forwardRef(() => VOneChipsTagsComponent),
  ],
  templateUrl: './v-one-table-cell.component.html',
  styleUrls: ['./v-one-table-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneTableCellComponent {
  readonly ICON_TYPE = IconType;

  config = input.required<CellConfig>();
  disabled = input<boolean>(false);

  actionClick = output<void>();
  toggleChange = output<boolean>();
  checkboxChange = output<boolean>();
  valueChange = output<string>();

  readonly toggleValue = signal<boolean>(false);
  readonly checkboxValue = signal<boolean>(false);
  readonly textValue = signal<string>('');
  readonly searchValue = signal<string>('');

  constructor() {
    // Sync checkbox and toggle values from config
    effect(() => {
      const cfg = this.config();
      if (cfg.checkboxValue !== undefined) {
        this.checkboxValue.set(cfg.checkboxValue);
      }
      if (cfg.toggleValue !== undefined) {
        this.toggleValue.set(cfg.toggleValue);
      }
      if (cfg.text !== undefined) {
        this.textValue.set(cfg.text);
      }
    });
  }

  readonly isInformativeCell = computed(() => {
    const variant = this.config().variant;
    const isWithRemark = variant === 'with-remark';
    return (
      ['single-line', 'multi-line', 'double-line', 'status', 'tag'].includes(
        variant,
      ) || isWithRemark
    );
  });

  readonly isEditableCell = computed(() => {
    const variant = this.config().variant;
    return ['dropdown', 'search', 'text'].includes(variant);
  });

  readonly isActionCell = computed(() => {
    const variant = this.config().variant;
    return ['action', 'action-icon', 'toggle', 'checkbox', 'icon'].includes(
      variant,
    );
  });

  handleAction(): void {
    if (!this.disabled()) {
      const config = this.config();
      if (config.onActionClick) {
        config.onActionClick();
      }
      this.actionClick.emit();
    }
  }

  handleToggle(): void {
    if (!this.disabled()) {
      this.toggleValue.update((v) => !v);
      const newValue = this.toggleValue();
      const config = this.config();
      if (config.onToggleChange) {
        config.onToggleChange(newValue);
      }
      this.toggleChange.emit(newValue);
    }
  }

  handleCheckbox(): void {
    if (!this.disabled()) {
      this.checkboxValue.update((v) => !v);
      const newValue = this.checkboxValue();
      const config = this.config();
      if (config.onCheckboxChange) {
        config.onCheckboxChange(newValue);
      }
      this.checkboxChange.emit(newValue);
    }
  }

  handleTextChange(value: string): void {
    this.textValue.set(value);
    this.valueChange.emit(value);
  }

  handleSearchChange(value: string): void {
    this.searchValue.set(value);
    this.valueChange.emit(value);
  }

  getCellRole(): string {
    const variant = this.config().variant;

    if (['toggle', 'checkbox'].includes(variant)) {
      return 'checkbox';
    }

    if (['action-icon', 'icon'].includes(variant)) {
      return 'button';
    }

    if (['dropdown', 'search', 'text'].includes(variant)) {
      return 'textbox';
    }

    return 'cell';
  }

  getAriaLabel(): string {
    const config = this.config();

    if (config.primaryText) {
      if (config.secondaryText) {
        return `${config.primaryText}, ${config.secondaryText}`;
      }
      return config.primaryText;
    }

    if (config.actionLabel) {
      return config.actionLabel;
    }

    return '';
  }
}
